/**
 * Input Mask Controller
 *
 * Provides input masking functionality for form fields without jQuery dependencies.
 * Automatically formats input as the user types and handles cursor positioning,
 * paste events, and keyboard shortcuts (copy/paste/cut).
 *
 * Supported formats:
 * - fein: 12-3456789 (Federal Employer Identification Number)
 * - phone: (123) 456-7890 (US phone number)
 * - zipcode: 12345 (5-digit ZIP code)
 * - ssn: 123-45-6789 (Social Security Number)
 * - date: MM/DD/YYYY (US date format)
 *
 * Usage:
 *
 * Simple Form with Rails:
 * ```slim
 * = f.input :fein, input_html: { data: { controller: "input-mask", input_mask_format_value: "fein" } }
 * ```
 *
 * Raw HTML:
 * ```html
 * <input type="text"
 *        data-controller="input-mask"
 *        data-input-mask-format-value="phone">
 *
 * <input type="text"
 *        data-controller="input-mask"
 *        data-input-mask-format-value="zipcode">
 * ```
 *
 * Features:
 * - Real-time formatting as user types
 * - Prevents invalid character input
 * - Preserves cursor position during formatting
 * - Handles paste events with automatic formatting
 * - Supports common keyboard shortcuts (Ctrl+A/C/V/X/Z)
 * - Works with both Ctrl (Windows/Linux) and Cmd (Mac) modifiers
 */

import { Controller } from '@hotwired/stimulus';

export default class InputMaskController extends Controller {
  static values = { format: String }

  connect() {
    this.applyMask();
    this.element.addEventListener('input', this.handleInput.bind(this));
    this.element.addEventListener('keydown', this.handleKeydown.bind(this));
    this.element.addEventListener('paste', this.handlePaste.bind(this));
  }

  disconnect() {
    this.element.removeEventListener('input', this.handleInput.bind(this));
    this.element.removeEventListener('keydown', this.handleKeydown.bind(this));
    this.element.removeEventListener('paste', this.handlePaste.bind(this));
  }

  applyMask() {
    const format = this.formatValue;
    if (format && this.element.value) {
      this.element.value = this.formatInput(this.element.value, format);
    }
  }

  handleInput(event) {
    const format = this.formatValue;
    if (!format) return;

    const cursorPosition = event.target.selectionStart;
    const oldValue = event.target.value;
    const newValue = this.formatInput(oldValue, format);

    if (oldValue !== newValue) {
      event.target.value = newValue;
      this.setCursorPosition(event.target, cursorPosition, oldValue, newValue);
    }
  }

  handleKeydown(event) {
    // Allow backspace, delete, tab, escape, enter, arrow keys, etc.
    const allowedKeys = ['Backspace', 'Tab', 'Escape', 'Enter', 'Delete', 'ArrowLeft', 'ArrowUp', 'ArrowRight', 'ArrowDown', 'Home', 'End'];
    if (allowedKeys.includes(event.key)) {
      return;
    }

    // Allow common keyboard shortcuts (Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+Z)
    if ((event.ctrlKey || event.metaKey) && ['a', 'c', 'v', 'x', 'z'].includes(event.key.toLowerCase())) {
      return;
    }

    // Ensure only numbers are allowed for numeric formats
    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  }

  handlePaste() {
    setTimeout(() => {
      const format = this.formatValue;
      if (format) {
        this.element.value = this.formatInput(this.element.value, format);
      }
    }, 0);
  }

  formatInput(value, format) {
    const digits = value.replace(/\D/g, '');

    switch (format) {
      case 'fein':
        return this.formatFEIN(digits);
      case 'phone':
        return this.formatPhone(digits);
      case 'zipcode':
        return this.formatZipCode(digits);
      case 'ssn':
        return this.formatSSN(digits);
      case 'date':
        return this.formatDate(digits);
      default:
        return value;
    }
  }

  formatFEIN(digits) {
    // 00-0000000
    if (digits.length === 0) return '';
    if (digits.length <= 2) return digits;
    return `${digits.slice(0, 2)}-${digits.slice(2, 9)}`;
  }

  formatPhone(digits) {
    // (000) 000-0000
    if (digits.length === 0) return '';
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  }

  formatZipCode(digits) {
    // 00000
    return digits.slice(0, 5);
  }

  formatSSN(digits) {
    // 000-00-0000
    if (digits.length === 0) return '';
    if (digits.length <= 3) return `${digits}`;
    if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5, 9)}`;
  }

  formatDate(digits) {
    // 00/00/0000
    if (digits.length === 0) return '';
    if (digits.length <= 2) return `${digits}`;
    if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
  }

  format

  setCursorPosition(input, cursorPosition, oldValue, newValue) {
    // Calculate new cursor position based on formatting changes
    let newCursorPosition = cursorPosition;

    // Count non-digit characters before cursor in old and new values
    const oldNonDigitsBefore = (oldValue.slice(0, cursorPosition).match(/\D/g) || []).length;
    const newNonDigitsBefore = (newValue.slice(0, cursorPosition).match(/\D/g) || []).length;

    // Adjust cursor position if formatting characters were added/removed
    newCursorPosition += (newNonDigitsBefore - oldNonDigitsBefore);

    // Ensure cursor position is within bounds
    newCursorPosition = Math.max(0, Math.min(newCursorPosition, newValue.length));

    setTimeout(() => {
      input.setSelectionRange(newCursorPosition, newCursorPosition);
    }, 0);
  }
}
